/**
 * Atlas — Project detail page: Kanban board, team, documents, financials, activity.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, propertyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ── Helpers ──────────────────────────────────────────────────────────────────

const PRIORITY_META = {
  low:      { label: 'Low',      color: '#6b7280', bg: '#f3f4f6' },
  medium:   { label: 'Medium',   color: '#d97706', bg: '#fef3c7' },
  high:     { label: 'High',     color: '#dc2626', bg: '#fee2e2' },
  critical: { label: 'Critical', color: '#7c3aed', bg: '#ede9fe' },
};

const TYPE_META = {
  task:      { icon: '☑', label: 'Task' },
  document:  { icon: '📄', label: 'Document' },
  approval:  { icon: '✔', label: 'Approval' },
  invoice:   { icon: '💳', label: 'Invoice' },
  issue:     { icon: '⚠', label: 'Issue' },
  checklist: { icon: '📋', label: 'Checklist' },
};

const STATUS_META = {
  open:        { label: 'Open',        color: '#374151', bg: '#f3f4f6' },
  in_progress: { label: 'In progress', color: '#2563eb', bg: '#dbeafe' },
  blocked:     { label: 'Blocked',     color: '#dc2626', bg: '#fee2e2' },
  review:      { label: 'Review',      color: '#d97706', bg: '#fef3c7' },
  done:        { label: 'Done',        color: '#15803d', bg: '#dcfce7' },
};

const FINANCIAL_STATUS_META = {
  pending:   { label: 'Pending',   color: '#d97706', bg: '#fef3c7' },
  approved:  { label: 'Approved',  color: '#2563eb', bg: '#dbeafe' },
  paid:      { label: 'Paid',      color: '#15803d', bg: '#dcfce7' },
  overdue:   { label: 'Overdue',   color: '#dc2626', bg: '#fee2e2' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' },
};

function fmtAud(n) {
  return n == null ? '—' : `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 0 })}`;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('board');
  const [editModal, setEditModal] = useState(false);
  const [propertyStatus, setPropertyStatus] = useState(null); // null | 'missing' | 'exists'
  const [addingProp, setAddingProp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await projectAPI.getBoard(id);
      setBoard(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!board?.address_line1 || !['owner', 'agent'].includes(user?.role)) return;
    propertyAPI.list().then(({ data }) => {
      const target = board.address_line1.toLowerCase().trim();
      const found = (data.items || []).some(
        (p) => (p.address_line1 || '').toLowerCase().trim() === target
      );
      setPropertyStatus(found ? 'exists' : 'missing');
    }).catch(() => {});
  }, [board, user?.role]);

  async function addToProperties() {
    setAddingProp(true);
    try {
      const { data: prop } = await propertyAPI.create({
        address_line1: board.address_line1,
        city: board.suburb || '',
        state: board.state || '',
        zip_code: board.postcode || '',
        country: board.country || 'AU',
      });
      setPropertyStatus('exists');
      navigate(`/properties/${prop.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add property');
    } finally {
      setAddingProp(false);
    }
  }

  if (loading) return <div style={{ display: 'grid', placeItems: 'center', height: 300, color: 'var(--ink-4)' }}>Loading project…</div>;
  if (error || !board) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ color: 'var(--crimson)', marginBottom: 16 }}>{error || 'Not found'}</div>
      <button className="btn ghost" onClick={() => navigate('/projects')}>← Back to projects</button>
    </div>
  );

  const address = [board.address_line1, board.suburb, board.state].filter(Boolean).join(', ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Project header */}
      <div style={{
        padding: '20px 28px 0', borderBottom: '1px solid var(--hairline)',
        background: 'var(--card)', flexShrink: 0,
      }}>
        <button className="btn ghost sm" onClick={() => navigate('/projects')} style={{ marginBottom: 12 }}>
          ← Projects
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: 1 }}>
            <h1 className="serif" style={{ fontSize: 26, fontWeight: 700, color: 'var(--forest)', margin: '0 0 4px' }}>
              {board.name}
            </h1>
            <div style={{ fontSize: 13, color: 'var(--ink-4)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {address && <span>📍 {address}</span>}
              {board.project_type && <span>{board.project_type}</span>}
              {board.budget && <span style={{ color: 'var(--forest)', fontWeight: 600 }}>{fmtAud(board.budget)} budget</span>}
              {board.target_completion && <span>Due {fmtDate(board.target_completion)}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>{board.member_count} team · {board.task_count} tasks</span>
            {propertyStatus === 'missing' && (
              <button
                className="btn ghost sm"
                onClick={addToProperties}
                disabled={addingProp}
                style={{ fontSize: 12 }}
              >
                {addingProp ? 'Adding…' : '+ Add to Properties'}
              </button>
            )}
            <button className="btn ghost sm" onClick={() => setEditModal(true)} style={{ fontSize: 12 }}>
              Edit project
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2 }}>
          {[
            { key: 'board',     label: 'Board' },
            { key: 'team',      label: `Team (${board.member_count})` },
            { key: 'documents', label: 'Documents' },
            { key: 'financials',label: 'Financials' },
            { key: 'activity',  label: 'Activity' },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '9px 16px', border: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: tab === key ? 700 : 400, background: 'none',
              color: tab === key ? 'var(--forest)' : 'var(--ink-4)',
              borderBottom: tab === key ? '2px solid var(--forest)' : '2px solid transparent',
              marginBottom: -1,
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {tab === 'board'     && <KanbanBoard board={board} projectId={id} onRefresh={load} />}
        {tab === 'team'      && <TeamTab projectId={id} members={board.members} stages={board.stages} onRefresh={load} />}
        {tab === 'documents' && <DocumentsTab projectId={id} />}
        {tab === 'financials'&& <FinancialsTab projectId={id} budget={board.budget} members={board.members} />}
        {tab === 'activity'  && <ActivityTab projectId={id} />}
      </div>

      {editModal && (
        <EditProjectModal
          projectId={id}
          board={board}
          onClose={() => setEditModal(false)}
          onSaved={() => { setEditModal(false); load(); }}
        />
      )}
    </div>
  );
}

// ── Kanban Board ──────────────────────────────────────────────────────────────

function KanbanBoard({ board, projectId, onRefresh }) {
  const [stages, setStages] = useState(board.stages);
  const [taskModal, setTaskModal] = useState(null);   // { stageId, task? }
  const [cardModal, setCardModal] = useState(null);   // task object for detail view
  const dragTaskId = useRef(null);
  const dragFromStageId = useRef(null);

  useEffect(() => { setStages(board.stages); }, [board]);

  function handleDragStart(e, taskId, stageId) {
    dragTaskId.current = taskId;
    dragFromStageId.current = stageId;
    e.dataTransfer.effectAllowed = 'move';
  }

  async function handleDrop(e, toStageId) {
    e.preventDefault();
    const taskId = dragTaskId.current;
    const fromStageId = dragFromStageId.current;
    if (!taskId || fromStageId === toStageId) return;

    // Optimistic update
    setStages((prev) => {
      const next = prev.map((s) => ({ ...s, tasks: [...s.tasks] }));
      let movedTask = null;
      for (const s of next) {
        const idx = s.tasks.findIndex((t) => t.id === taskId);
        if (idx !== -1) { movedTask = s.tasks.splice(idx, 1)[0]; break; }
      }
      if (movedTask) {
        const target = next.find((s) => s.id === toStageId);
        if (target) target.tasks.push({ ...movedTask, stage_id: toStageId });
      }
      return next;
    });

    try {
      await projectAPI.updateTask(projectId, taskId, { stage_id: toStageId });
    } catch {
      onRefresh(); // revert on error
    }
    dragTaskId.current = null;
    dragFromStageId.current = null;
  }

  async function handleStatusChange(taskId, newStatus) {
    try {
      await projectAPI.updateTask(projectId, taskId, { status: newStatus });
      onRefresh();
    } catch {}
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '20px 28px' }}>
      <div style={{ display: 'flex', gap: 14, minWidth: 'max-content', height: '100%' }}>
        {stages.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            projectId={projectId}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            onAddTask={() => setTaskModal({ stageId: stage.id })}
            onCardClick={(task) => setCardModal(task)}
            onStatusChange={handleStatusChange}
            onRefresh={onRefresh}
          />
        ))}

        {/* Add stage button */}
        <AddStageButton projectId={projectId} stageCount={stages.length} onAdded={onRefresh} />
      </div>

      {taskModal && (
        <TaskFormModal
          projectId={projectId}
          stageId={taskModal.stageId}
          members={board.members}
          onClose={() => setTaskModal(null)}
          onSaved={() => { setTaskModal(null); onRefresh(); }}
        />
      )}

      {cardModal && (
        <CardDetailModal
          task={cardModal}
          projectId={projectId}
          members={board.members}
          onClose={() => setCardModal(null)}
          onRefresh={() => { setCardModal(null); onRefresh(); }}
        />
      )}
    </div>
  );
}

function KanbanColumn({ stage, projectId, onDragStart, onDrop, onAddTask, onCardClick, onStatusChange, onRefresh }) {
  const [over, setOver] = useState(false);
  const activeTasks = stage.tasks.filter((t) => !t.is_deleted);
  const deletedTasks = stage.tasks.filter((t) => t.is_deleted);

  return (
    <div
      style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { setOver(false); onDrop(e, stage.id); }}
    >
      {/* Column header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
        padding: '0 4px',
      }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: stage.color, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', flex: 1 }}>{stage.name}</span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: 'var(--ink-4)',
          background: 'var(--paper)', borderRadius: 8, padding: '1px 7px',
        }}>{activeTasks.length}</span>
      </div>

      {/* Cards */}
      <div style={{
        flex: 1, minHeight: 80, borderRadius: 8, padding: '6px 4px',
        background: over ? 'rgba(31,58,46,0.04)' : 'transparent',
        border: `2px dashed ${over ? 'var(--forest)' : 'transparent'}`,
        transition: 'background 0.1s, border-color 0.1s',
        overflowY: 'auto',
      }}>
        {activeTasks.map((task) => (
          <KanbanCard
            key={task.id}
            task={task}
            projectId={projectId}
            onDragStart={(e) => onDragStart(e, task.id, stage.id)}
            onClick={() => onCardClick(task)}
            onRefresh={onRefresh}
          />
        ))}

        {activeTasks.length === 0 && !over && deletedTasks.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-4)', textAlign: 'center', padding: '20px 8px', fontStyle: 'italic' }}>
            Drop cards here
          </div>
        )}

        {/* Deleted cards — separator + greyed cards at bottom */}
        {deletedTasks.length > 0 && (
          <>
            {activeTasks.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '10px 4px 6px', opacity: 0.5 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
                <span style={{ fontSize: 10, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>deleted</span>
                <div style={{ flex: 1, height: 1, background: 'var(--hairline)' }} />
              </div>
            )}
            {deletedTasks.map((task) => (
              <KanbanCard
                key={task.id}
                task={task}
                projectId={projectId}
                onDragStart={() => {}}
                onClick={() => onCardClick(task)}
                onRefresh={onRefresh}
              />
            ))}
          </>
        )}
      </div>

      {/* Add task */}
      <button
        onClick={onAddTask}
        style={{
          marginTop: 8, padding: '7px 10px', background: 'none',
          border: '1px dashed var(--hairline)', borderRadius: 6,
          fontSize: 12, color: 'var(--ink-4)', cursor: 'pointer', textAlign: 'left',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--paper)'; e.currentTarget.style.color = 'var(--forest)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--ink-4)'; }}
      >
        + Add card
      </button>
    </div>
  );
}

function KanbanCard({ task, projectId, onDragStart, onClick, onRefresh }) {
  const [recovering, setRecovering] = useState(false);
  const pm = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const tm = TYPE_META[task.task_type] || TYPE_META.task;
  const sm = STATUS_META[task.status] || STATUS_META.open;
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';
  const checkItems = task.checklist_items || [];
  const checkedCount = checkItems.filter((i) => i.checked).length;
  const checkProgress = checkItems.length > 0 ? (checkedCount / checkItems.length) * 100 : 0;

  async function handleRecover(e) {
    e.stopPropagation();
    setRecovering(true);
    try {
      await projectAPI.recoverTask(projectId, task.id);
      onRefresh();
    } catch {
      setRecovering(false);
    }
  }

  if (task.is_deleted) {
    return (
      <div
        onClick={onClick}
        style={{
          background: '#f9fafb', border: '1px dashed #d1d5db',
          borderRadius: 8, padding: '8px 12px', marginBottom: 8,
          cursor: 'pointer', userSelect: 'none', opacity: 0.75,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#fee2e2', color: '#dc2626', fontWeight: 600 }}>Deleted</span>
          <span style={{ fontSize: 11, color: '#9ca3af', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'line-through' }}>
            {task.title}
          </span>
        </div>
        <button
          onClick={handleRecover}
          disabled={recovering}
          style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 6,
            border: '1px solid #d1d5db', background: '#fff',
            color: '#374151', cursor: 'pointer', fontWeight: 500,
          }}
        >
          {recovering ? 'Recovering…' : '↩ Recover'}
        </button>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      style={{
        background: 'var(--card)', border: '1px solid var(--hairline)',
        borderRadius: 8, padding: '10px 12px', marginBottom: 8,
        cursor: 'grab', userSelect: 'none',
        borderLeft: `3px solid ${pm.color}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,0,0,0.1)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
    >
      {/* Type + status row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>{tm.icon} {tm.label}</span>
        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: sm.bg, color: sm.color, fontWeight: 600 }}>
          {sm.label}
        </span>
      </div>

      {/* Title */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.4, marginBottom: 6 }}>
        {task.title}
      </div>

      {/* Professional type */}
      {task.professional_type && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 6, lineHeight: 1.3 }}>
          👷 {task.professional_type}
        </div>
      )}

      {/* Checklist progress */}
      {checkItems.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--ink-4)', marginBottom: 3 }}>
            <span>Checklist</span>
            <span>{checkedCount}/{checkItems.length}</span>
          </div>
          <div style={{ height: 3, background: 'var(--hairline)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', background: checkProgress === 100 ? '#15803d' : 'var(--forest)', width: `${checkProgress}%`, transition: 'width 0.2s' }} />
          </div>
        </div>
      )}

      {/* Planned budget */}
      {task.planned_budget != null && (
        <div style={{ fontSize: 11, color: 'var(--forest)', fontWeight: 600, marginBottom: 6 }}>
          {fmtAud(task.planned_budget)} planned
          {task.quoted_amount != null && <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}> · {fmtAud(task.quoted_amount)} quoted</span>}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
        <span style={{ padding: '1px 6px', borderRadius: 8, background: pm.bg, color: pm.color, fontWeight: 600 }}>
          {pm.label}
        </span>
        {task.due_date && (
          <span style={{ color: isOverdue ? 'var(--crimson)' : 'var(--ink-4)', marginLeft: 'auto' }}>
            {isOverdue ? '⚠ ' : ''}
            {fmtDate(task.due_date)}
          </span>
        )}
        {task.comment_count > 0 && (
          <span style={{ color: 'var(--ink-4)', marginLeft: task.due_date ? 0 : 'auto' }}>
            💬 {task.comment_count}
          </span>
        )}
        {task.assigned_member_name && (
          <span style={{
            width: 20, height: 20, borderRadius: '50%', background: 'var(--forest)',
            color: '#f5f1e8', fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginLeft: 'auto', flexShrink: 0,
          }}>
            {task.assigned_member_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>

      {/* Tags */}
      {task.tags?.filter((t) => t !== 'checklist').length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 7 }}>
          {task.tags.filter((t) => t !== 'checklist').slice(0, 3).map((t) => (
            <span key={t} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#f3f4f6', color: '#374151' }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function AddStageButton({ projectId, stageCount, onAdded }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await projectAPI.createStage(projectId, { name: name.trim(), order: stageCount });
      setName('');
      setEditing(false);
      onAdded();
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div style={{ width: 220, flexShrink: 0 }}>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="Stage name…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid var(--forest)', borderRadius: 6, fontSize: 13 }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button className="btn primary sm" onClick={save} disabled={saving || !name.trim()}>Add</button>
          <button className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      style={{
        width: 220, flexShrink: 0, height: 40, border: '1px dashed var(--hairline)',
        borderRadius: 8, background: 'none', fontSize: 13, color: 'var(--ink-4)',
        cursor: 'pointer', alignSelf: 'flex-start',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--forest)'; e.currentTarget.style.color = 'var(--forest)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--hairline)'; e.currentTarget.style.color = 'var(--ink-4)'; }}
    >
      + Add stage
    </button>
  );
}

// ── Task Form Modal ────────────────────────────────────────────────────────────

function TaskFormModal({ projectId, stageId, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', description: '', task_type: 'task',
    priority: 'medium', due_date: '', tags: '',
    assigned_member_id: '',
    professional_type: '',
    planned_budget: '',
    quoted_amount: '',
    actual_invoice: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title required'); return; }
    setSaving(true);
    setError('');
    try {
      await projectAPI.createTask(projectId, stageId, {
        title: form.title.trim(),
        description: form.description || undefined,
        task_type: form.task_type,
        priority: form.priority,
        due_date: form.due_date || undefined,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        assigned_member_id: form.assigned_member_id || undefined,
        professional_type: form.professional_type || undefined,
        planned_budget: form.planned_budget !== '' ? parseFloat(form.planned_budget) : undefined,
        quoted_amount: form.quoted_amount !== '' ? parseFloat(form.quoted_amount) : undefined,
        actual_invoice: form.actual_invoice !== '' ? parseFloat(form.actual_invoice) : undefined,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  }

  const fs = { width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, background: 'var(--card)', fontSize: 13 };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="serif" style={{ fontSize: 18, color: 'var(--forest)', fontWeight: 600, margin: 0 }}>Add card</h3>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
            {error && <div style={{ color: 'var(--crimson)', fontSize: 13 }}>{error}</div>}
            <div className="field">
              <label>Title *</label>
              <input style={fs} value={form.title} onChange={set('title')} placeholder="What needs to happen?" autoFocus />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label>Type</label>
                <select style={fs} value={form.task_type} onChange={set('task_type')}>
                  {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Priority</label>
                <select style={fs} value={form.priority} onChange={set('priority')}>
                  {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label>Due date</label>
                <input style={fs} type="date" value={form.due_date} onChange={set('due_date')} />
              </div>
              <div className="field">
                <label>Assign to</label>
                <select style={fs} value={form.assigned_member_id} onChange={set('assigned_member_id')}>
                  <option value="">— unassigned —</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name} {m.role ? `(${m.role})` : ''}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Description</label>
              <textarea value={form.description} onChange={set('description')} rows={3}
                style={{ ...fs, height: 72, padding: '7px 10px', resize: 'vertical' }}
                placeholder="Optional details…" />
            </div>
            <div className="field">
              <label>Tags (comma-separated)</label>
              <input style={fs} value={form.tags} onChange={set('tags')} placeholder="e.g. permit, urgent, council" />
            </div>
            <div className="field">
              <label>Professional type</label>
              <input style={fs} value={form.professional_type} onChange={set('professional_type')} placeholder="e.g. Builder, Architect, Engineer" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="field">
                <label>Planned budget ($)</label>
                <input style={fs} type="number" min="0" step="0.01" value={form.planned_budget} onChange={set('planned_budget')} placeholder="0.00" />
              </div>
              <div className="field">
                <label>Quote ($)</label>
                <input style={fs} type="number" min="0" step="0.01" value={form.quoted_amount} onChange={set('quoted_amount')} placeholder="0.00" />
              </div>
              <div className="field">
                <label>Actual invoice ($)</label>
                <input style={fs} type="number" min="0" step="0.01" value={form.actual_invoice} onChange={set('actual_invoice')} placeholder="0.00" />
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Add card'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Card Detail Modal ─────────────────────────────────────────────────────────

function CardDetailModal({ task, projectId, members, onClose, onRefresh }) {
  const [localTask, setLocalTask] = useState(task);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    title: task.title,
    description: task.description || '',
    task_type: task.task_type,
    priority: task.priority,
    due_date: task.due_date ? task.due_date.slice(0, 10) : '',
    assigned_member_id: task.assigned_member_id || '',
    tags: (task.tags || []).filter((t) => t !== 'checklist').join(', '),
    professional_type: task.professional_type || '',
    what_to_expect: task.what_to_expect || '',
    key_monitors: task.key_monitors || '',
    planned_budget: task.planned_budget != null ? String(task.planned_budget) : '',
    quoted_amount: task.quoted_amount != null ? String(task.quoted_amount) : '',
    actual_invoice: task.actual_invoice != null ? String(task.actual_invoice) : '',
  });
  const [editItems, setEditItems] = useState((task.checklist_items || []).map((i) => ({ ...i })));
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [recovering, setRecovering] = useState(false);

  const set = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    projectAPI.listComments(projectId, task.id).then(({ data }) => setComments(data));
  }, [projectId, task.id]);

  async function handleDelete() {
    if (!window.confirm('Delete this card? It will stay visible at the bottom of the column and can be recovered.')) return;
    setDeleting(true);
    try {
      await projectAPI.deleteTask(projectId, task.id);
      onRefresh();
    } catch {
      setDeleting(false);
    }
  }

  async function handleRecover() {
    setRecovering(true);
    try {
      await projectAPI.recoverTask(projectId, task.id);
      onRefresh();
    } catch {
      setRecovering(false);
    }
  }

  async function updateStatus(s) {
    setLocalTask((t) => ({ ...t, status: s }));
    await projectAPI.updateTask(projectId, task.id, { status: s });
  }

  async function toggleChecklistItem(idx) {
    const newItems = localTask.checklist_items.map((item, i) =>
      i === idx ? { ...item, checked: !item.checked } : item
    );
    setLocalTask((t) => ({ ...t, checklist_items: newItems }));
    try {
      await projectAPI.updateTask(projectId, task.id, { checklist_items: newItems });
    } catch {
      setLocalTask((t) => ({ ...t, checklist_items: localTask.checklist_items }));
    }
  }

  async function saveEdit(e) {
    e.preventDefault();
    if (!editForm.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description || undefined,
        task_type: editForm.task_type,
        priority: editForm.priority,
        due_date: editForm.due_date || undefined,
        assigned_member_id: editForm.assigned_member_id || undefined,
        tags: editForm.tags ? editForm.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        professional_type: editForm.professional_type || undefined,
        what_to_expect: editForm.what_to_expect || undefined,
        key_monitors: editForm.key_monitors || undefined,
        planned_budget: editForm.planned_budget !== '' ? parseFloat(editForm.planned_budget) : null,
        quoted_amount: editForm.quoted_amount !== '' ? parseFloat(editForm.quoted_amount) : null,
        actual_invoice: editForm.actual_invoice !== '' ? parseFloat(editForm.actual_invoice) : null,
        checklist_items: editItems.filter((i) => i.text.trim()),
      };
      await projectAPI.updateTask(projectId, task.id, payload);
      const assignedMember = members.find((m) => String(m.id) === String(editForm.assigned_member_id));
      setLocalTask((t) => ({
        ...t, ...payload,
        checklist_items: editItems.filter((i) => i.text.trim()),
        assigned_member_name: assignedMember ? assignedMember.name : t.assigned_member_name,
      }));
      setEditMode(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function postComment(e) {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await projectAPI.addComment(projectId, task.id, newComment.trim());
      setComments((c) => [...c, data]);
      setNewComment('');
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteTask() {
    if (!window.confirm('Delete this card?')) return;
    await projectAPI.deleteTask(projectId, task.id);
    onRefresh();
  }

  const pm = PRIORITY_META[localTask.priority] || PRIORITY_META.medium;
  const tm = TYPE_META[localTask.task_type] || TYPE_META.task;
  const sm = STATUS_META[localTask.status] || STATUS_META.open;
  const checkItems = localTask.checklist_items || [];
  const checkedCount = checkItems.filter((i) => i.checked).length;
  const checkProgress = checkItems.length > 0 ? (checkedCount / checkItems.length) * 100 : 0;

  const inputStyle = { width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, background: 'var(--card)', fontSize: 13 };
  const taStyle = { ...inputStyle, height: 72, padding: '7px 10px', resize: 'vertical' };
  const lbl = (text) => <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{text}</div>;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-head" style={{ borderLeft: `4px solid ${pm.color}`, paddingLeft: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4 }}>
              {tm.icon} {tm.label} · {pm.label} priority
            </div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
              {localTask.title}
            </h3>
          </div>
          {!editMode && !localTask.is_deleted && (
            <button className="btn ghost sm" onClick={() => setEditMode(true)}>Edit</button>
          )}
          {!editMode && !localTask.is_deleted && (
            <button
              className="btn ghost sm"
              onClick={handleDelete}
              disabled={deleting}
              style={{ color: 'var(--crimson)' }}
            >
              {deleting ? '…' : 'Delete'}
            </button>
          )}
          {!editMode && localTask.is_deleted && (
            <button
              className="btn ghost sm"
              onClick={handleRecover}
              disabled={recovering}
              style={{ color: 'var(--forest)', fontWeight: 600 }}
            >
              {recovering ? 'Recovering…' : '↩ Recover'}
            </button>
          )}
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>

        {/* ── Deleted banner ── */}
        {localTask.is_deleted && (
          <div style={{
            margin: '0 20px 0', padding: '8px 14px',
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
            fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>⚠</span>
            <span>This card has been deleted. Click <strong>↩ Recover</strong> to restore it to the active board.</span>
          </div>
        )}

        {/* ── Edit mode ── */}
        {editMode ? (
          <form onSubmit={saveEdit}>
            <div className="modal-body" style={{ overflowY: 'auto', maxHeight: '74vh', display: 'grid', gap: 14 }}>

              <div className="field">{lbl('Title *')}<input style={inputStyle} value={editForm.title} onChange={set('title')} autoFocus required /></div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="field">
                  {lbl('Type')}
                  <select style={inputStyle} value={editForm.task_type} onChange={set('task_type')}>
                    {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                  </select>
                </div>
                <div className="field">
                  {lbl('Priority')}
                  <select style={inputStyle} value={editForm.priority} onChange={set('priority')}>
                    {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="field">
                  {lbl('Due date')}
                  <input style={inputStyle} type="date" value={editForm.due_date} onChange={set('due_date')} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="field">
                  {lbl('Assign to')}
                  <select style={inputStyle} value={editForm.assigned_member_id} onChange={set('assigned_member_id')}>
                    <option value="">— unassigned —</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.name}{m.role ? ` (${m.role})` : ''}</option>)}
                  </select>
                </div>
                <div className="field">
                  {lbl('Tags (comma-separated)')}
                  <input style={inputStyle} value={editForm.tags} onChange={set('tags')} placeholder="e.g. urgent, permit" />
                </div>
              </div>

              <div className="field">{lbl('Description')}<textarea style={taStyle} value={editForm.description} onChange={set('description')} placeholder="Notes…" /></div>

              {/* Context fields */}
              <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Context</div>
                <div className="field" style={{ marginBottom: 12 }}>{lbl('Professional type')}<input style={inputStyle} value={editForm.professional_type} onChange={set('professional_type')} placeholder="e.g. Architect · Structural Engineer" /></div>
                <div className="field" style={{ marginBottom: 12 }}>{lbl('What to expect')}<textarea style={{ ...taStyle, height: 80 }} value={editForm.what_to_expect} onChange={set('what_to_expect')} placeholder="Brief overview of this stage…" /></div>
                <div className="field">{lbl('Key things to monitor')}<textarea style={{ ...taStyle, height: 80 }} value={editForm.key_monitors} onChange={set('key_monitors')} placeholder="• Item one&#10;• Item two" /></div>
              </div>

              {/* Financial fields */}
              <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Financials</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="field">{lbl('Planned budget ($)')}<input style={inputStyle} type="number" value={editForm.planned_budget} onChange={set('planned_budget')} placeholder="0.00" /></div>
                  <div className="field">{lbl('Quote ($)')}<input style={inputStyle} type="number" value={editForm.quoted_amount} onChange={set('quoted_amount')} placeholder="0.00" /></div>
                  <div className="field">{lbl('Actual invoice ($)')}<input style={inputStyle} type="number" value={editForm.actual_invoice} onChange={set('actual_invoice')} placeholder="0.00" /></div>
                </div>
              </div>

              {/* Checklist items editor */}
              <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Checklist items</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {editItems.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => setEditItems((prev) => prev.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it))}
                        style={{ flexShrink: 0, accentColor: 'var(--forest)' }}
                      />
                      <input
                        value={item.text}
                        onChange={(e) => setEditItems((prev) => prev.map((it, i) => i === idx ? { ...it, text: e.target.value } : it))}
                        style={{ flex: 1, height: 30, padding: '0 8px', border: '1px solid var(--hairline)', borderRadius: 4, fontSize: 13, background: 'var(--card)' }}
                      />
                      <button
                        type="button"
                        onClick={() => setEditItems((prev) => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crimson)', fontSize: 14, padding: '0 4px', flexShrink: 0 }}
                      >✕</button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setEditItems((prev) => [...prev, { text: '', checked: false }])}
                  style={{ marginTop: 8, fontSize: 12, color: 'var(--forest)', background: 'none', border: '1px dashed var(--hairline)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer' }}
                >
                  + Add item
                </button>
              </div>
            </div>

            <div className="modal-foot">
              <button type="button" className="btn ghost sm" style={{ color: 'var(--crimson)' }} onClick={deleteTask}>Delete</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn ghost" onClick={() => setEditMode(false)}>Cancel</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            </div>
          </form>
        ) : (

        /* ── View mode ── */
        <>
          <div className="modal-body" style={{ overflowY: 'auto', maxHeight: '74vh' }}>

            {/* Status chips */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <button key={k} onClick={() => updateStatus(k)} style={{
                  padding: '4px 12px', borderRadius: 16, fontSize: 12, cursor: 'pointer',
                  fontWeight: localTask.status === k ? 700 : 400,
                  border: `1px solid ${localTask.status === k ? v.color : 'var(--hairline)'}`,
                  background: localTask.status === k ? v.bg : 'transparent',
                  color: localTask.status === k ? v.color : 'var(--ink-4)',
                }}>{v.label}</button>
              ))}
            </div>

            {/* Context panel */}
            {(localTask.professional_type || localTask.what_to_expect || localTask.key_monitors) && (
              <div style={{ background: 'rgba(31,58,46,0.04)', border: '1px solid rgba(31,58,46,0.1)', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                {localTask.professional_type && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: localTask.what_to_expect || localTask.key_monitors ? 10 : 0 }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-4)', width: 90, flexShrink: 0, paddingTop: 1 }}>👷 Pro type</span>
                    <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500, lineHeight: 1.4 }}>{localTask.professional_type}</span>
                  </div>
                )}
                {localTask.what_to_expect && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: localTask.key_monitors ? 10 : 0 }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-4)', width: 90, flexShrink: 0, paddingTop: 1 }}>📋 Expect</span>
                    <span style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{localTask.what_to_expect}</span>
                  </div>
                )}
                {localTask.key_monitors && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-4)', width: 90, flexShrink: 0, paddingTop: 1 }}>👁 Monitor</span>
                    <pre style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{localTask.key_monitors}</pre>
                  </div>
                )}
              </div>
            )}

            {/* Checklist */}
            {checkItems.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)' }}>Checklist</span>
                  <span style={{ fontSize: 12, color: checkedCount === checkItems.length ? '#15803d' : 'var(--ink-4)', fontWeight: 600 }}>
                    {checkedCount}/{checkItems.length}
                  </span>
                  <div style={{ flex: 1, height: 5, background: 'var(--hairline)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: checkProgress === 100 ? '#15803d' : 'var(--forest)', width: `${checkProgress}%`, transition: 'width 0.3s' }} />
                  </div>
                  {checkProgress === 100 && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 700 }}>✓ Complete</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {checkItems.map((item, idx) => (
                    <label key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '3px 0' }}>
                      <input
                        type="checkbox"
                        checked={item.checked}
                        onChange={() => toggleChecklistItem(idx)}
                        style={{ marginTop: 2, flexShrink: 0, accentColor: 'var(--forest)' }}
                      />
                      <span style={{ fontSize: 13, color: item.checked ? 'var(--ink-4)' : 'var(--ink)', textDecoration: item.checked ? 'line-through' : 'none', lineHeight: 1.4 }}>
                        {item.text}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Financial summary */}
            {(localTask.planned_budget != null || localTask.quoted_amount != null || localTask.actual_invoice != null) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
                {[
                  { label: 'Planned Budget', value: localTask.planned_budget, color: 'var(--forest)' },
                  { label: 'Quote',           value: localTask.quoted_amount,  color: '#2563eb' },
                  {
                    label: 'Actual Invoice',
                    value: localTask.actual_invoice,
                    color: localTask.actual_invoice != null && localTask.planned_budget != null && localTask.actual_invoice > localTask.planned_budget ? '#dc2626' : '#15803d',
                  },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: 'var(--paper)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)', marginBottom: 5 }}>{label}</div>
                    <div className="serif" style={{ fontSize: 18, fontWeight: 700, color: value != null ? color : 'var(--ink-4)' }}>
                      {value != null ? fmtAud(value) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Description, due, assigned, tags */}
            {localTask.description && (
              <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.6, margin: '0 0 16px' }}>{localTask.description}</p>
            )}
            {(localTask.due_date || localTask.assigned_member_name) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {localTask.due_date && (
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 3 }}>Due date</div>
                    <div style={{ fontSize: 13 }}>{fmtDate(localTask.due_date)}</div>
                  </div>
                )}
                {localTask.assigned_member_name && (
                  <div>
                    <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 3 }}>Assigned to</div>
                    <div style={{ fontSize: 13 }}>{localTask.assigned_member_name}</div>
                  </div>
                )}
              </div>
            )}
            {localTask.tags?.filter((t) => t !== 'checklist').length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 20 }}>
                {localTask.tags.filter((t) => t !== 'checklist').map((t) => (
                  <span key={t} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: '#f3f4f6', color: '#374151' }}>{t}</span>
                ))}
              </div>
            )}

            {/* Comments */}
            <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', marginBottom: 12 }}>Comments ({comments.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14, maxHeight: 200, overflowY: 'auto' }}>
                {comments.map((c) => (
                  <div key={c.id} style={{ background: '#f9fafb', borderRadius: 6, padding: '9px 12px' }}>
                    <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4 }}>
                      <strong>{c.author_name}</strong> · {fmtDate(c.created_at)}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>{c.body}</div>
                  </div>
                ))}
                {comments.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-4)', fontStyle: 'italic' }}>No comments yet.</div>}
              </div>
              <form onSubmit={postComment} style={{ display: 'flex', gap: 8 }}>
                <input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment…"
                  style={{ flex: 1, height: 34, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, fontSize: 13, background: 'var(--card)' }}
                />
                <button type="submit" className="btn primary sm" disabled={submitting || !newComment.trim()}>Post</button>
              </form>
            </div>
          </div>

          <div className="modal-foot">
            <button className="btn ghost sm" style={{ color: 'var(--crimson)' }} onClick={deleteTask}>Delete</button>
            <button className="btn ghost" onClick={onClose}>Close</button>
          </div>
        </>
        )}
      </div>
    </div>
  );
}

// ── Team Tab ──────────────────────────────────────────────────────────────────

function TeamTab({ projectId, members, stages, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recStage, setRecStage] = useState(stages[0]?.name || '');
  const [recLoading, setRecLoading] = useState(false);

  const loadRecs = useCallback(async (stageName) => {
    if (!stageName) return;
    setRecLoading(true);
    try {
      const { data } = await projectAPI.getRecommendations(projectId, stageName);
      setRecommendations(data);
    } finally {
      setRecLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (recStage) loadRecs(recStage); }, [recStage, loadRecs]);

  return (
    <div style={{ padding: '24px 28px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 28, maxWidth: 1100 }}>

        {/* Left: team members */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)' }}>Project Team</div>
            <button className="btn primary sm" onClick={() => setShowAdd(true)}>+ Add member</button>
          </div>

          {members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-4)', fontSize: 14 }}>
              No team members yet. Add people to your project.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {members.map((m) => (
                <MemberCard key={m.id} member={m} projectId={projectId} onRefresh={onRefresh} />
              ))}
            </div>
          )}
        </div>

        {/* Right: recommendations */}
        <div>
          <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>
            Recommended Pros
          </div>
          <div style={{ marginBottom: 12 }}>
            <select
              value={recStage}
              onChange={(e) => setRecStage(e.target.value)}
              style={{ width: '100%', height: 34, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, fontSize: 13, background: 'var(--card)' }}
            >
              {stages.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          {recLoading ? (
            <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>
          ) : recommendations.length === 0 ? (
            <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>No marketplace pros found for this stage.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recommendations.map((pro) => (
                <RecommendedProCard key={pro.id} pro={pro} projectId={projectId} onAdded={onRefresh} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <AddMemberModal projectId={projectId} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); onRefresh(); }} />
      )}
    </div>
  );
}

function MemberCard({ member, projectId, onRefresh }) {
  async function remove() {
    if (!window.confirm(`Remove ${member.name} from project?`)) return;
    await projectAPI.removeMember(projectId, member.id);
    onRefresh();
  }

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 38, height: 38, borderRadius: '50%', background: 'var(--forest)',
        color: '#f5f1e8', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, flexShrink: 0,
      }}>
        {member.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{member.name}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
          {[member.role, member.company].filter(Boolean).join(' · ')}
          {member.email && ` · ${member.email}`}
        </div>
      </div>
      {member.is_external && (
        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#f3f4f6', color: '#6b7280' }}>External</span>
      )}
      <button className="btn ghost sm" onClick={remove} style={{ color: 'var(--crimson)', fontSize: 11 }}>Remove</button>
    </div>
  );
}

function RecommendedProCard({ pro, projectId, onAdded }) {
  const [adding, setAdding] = useState(false);

  async function add() {
    setAdding(true);
    try {
      await projectAPI.addMember(projectId, {
        name: pro.business_name,
        role: pro.trade,
        pro_id: pro.id,
      });
      onAdded();
    } finally {
      setAdding(false);
    }
  }

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 1 }}>{pro.business_name}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
          {pro.trade}
          {(pro.suburb || pro.state) && ` · ${[pro.suburb, pro.state].filter(Boolean).join(', ')}`}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
          ★ {pro.rating.toFixed(1)} · {pro.review_count} reviews
          {pro.is_atlas_verified && ' · ✓ Verified'}
        </div>
      </div>
      <button className="btn ghost sm" style={{ fontSize: 11 }} onClick={add} disabled={adding}>
        {adding ? '…' : '+ Add'}
      </button>
    </div>
  );
}

function AddMemberModal({ projectId, onClose, onAdded }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', company: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fs = { width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, background: 'var(--card)', fontSize: 13 };

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name required'); return; }
    setSaving(true);
    setError('');
    try {
      await projectAPI.addMember(projectId, {
        name: form.name.trim(),
        email: form.email || undefined,
        phone: form.phone || undefined,
        role: form.role || undefined,
        company: form.company || undefined,
        notes: form.notes || undefined,
      });
      onAdded();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="serif" style={{ fontSize: 18, color: 'var(--forest)', fontWeight: 600, margin: 0 }}>Add team member</h3>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
            {error && <div style={{ color: 'var(--crimson)', fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field"><label>Name *</label><input style={fs} value={form.name} onChange={set('name')} /></div>
              <div className="field"><label>Role</label><input style={fs} value={form.role} onChange={set('role')} placeholder="e.g. Architect" /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field"><label>Email</label><input style={fs} type="email" value={form.email} onChange={set('email')} /></div>
              <div className="field"><label>Phone</label><input style={fs} value={form.phone} onChange={set('phone')} /></div>
            </div>
            <div className="field"><label>Company</label><input style={fs} value={form.company} onChange={set('company')} /></div>
            <div className="field">
              <label>Notes</label>
              <textarea value={form.notes} onChange={set('notes')} rows={2} style={{ ...fs, height: 60, padding: '7px 10px', resize: 'vertical' }} />
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Add member'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Documents Tab ─────────────────────────────────────────────────────────────

function DocumentsTab({ projectId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectAPI.listDocuments(projectId)
      .then(({ data }) => setDocs(data))
      .finally(() => setLoading(false));
  }, [projectId]);

  const DOC_ICONS = { plan: '📐', permit: '📋', invoice: '💳', photo: '🖼', report: '📊', certificate: '🏅', contract: '📝', other: '📄' };

  return (
    <div style={{ padding: '24px 28px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)' }}>Documents</div>
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Upload support via task attachments</div>
      </div>

      {loading ? (
        <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>
      ) : docs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
          <div style={{ fontSize: 15, color: 'var(--ink-4)' }}>No documents yet.</div>
          <div style={{ fontSize: 13, color: 'var(--ink-4)', marginTop: 6 }}>Documents attached to tasks will appear here.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {docs.map((doc) => (
            <a key={doc.id} href={doc.file_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <div style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 8, padding: 14, cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = ''}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{DOC_ICONS[doc.doc_type] || '📄'}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {doc.original_filename}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                  {doc.doc_type} · {fmtDate(doc.created_at)}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Financials Tab ────────────────────────────────────────────────────────────

function FinancialsTab({ projectId, budget, members }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await projectAPI.listFinancials(projectId);
      setEntries(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const totalCommitted = entries.filter((e) => ['invoice', 'quote', 'expense'].includes(e.entry_type)).reduce((s, e) => s + e.amount, 0);
  const totalPaid = entries.filter((e) => e.status === 'paid').reduce((s, e) => s + e.amount, 0);
  const remaining = budget ? budget - totalCommitted : null;

  return (
    <div style={{ padding: '24px 28px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)' }}>Financials</div>
        <button className="btn primary sm" onClick={() => setShowAdd(true)}>+ Add entry</button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Budget', value: fmtAud(budget), color: 'var(--forest)' },
          { label: 'Committed', value: fmtAud(totalCommitted), color: '#d97706' },
          { label: 'Paid', value: fmtAud(totalPaid), color: '#2563eb' },
          { label: 'Remaining', value: fmtAud(remaining), color: remaining != null && remaining < 0 ? '#dc2626' : '#15803d' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '14px 16px' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-4)', marginBottom: 6 }}>{label}</div>
            <div className="serif" style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Entries table */}
      {loading ? (
        <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-4)', fontSize: 14 }}>No financial entries yet.</div>
      ) : (
        <div style={{ background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid var(--hairline)' }}>
                {['Type', 'Description', 'Assignee', 'Amount', 'Status', 'Due'].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const sm = FINANCIAL_STATUS_META[e.status] || FINANCIAL_STATUS_META.pending;
                return (
                  <tr key={e.id} style={{ borderBottom: i < entries.length - 1 ? '1px solid var(--hairline)' : 'none' }}>
                    <td style={{ padding: '10px 14px', textTransform: 'capitalize', color: 'var(--ink-3)' }}>{e.entry_type}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{e.description}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-4)' }}>{e.member_name || '—'}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--forest)' }}>{fmtAud(e.amount)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 8, background: sm.bg, color: sm.color, fontSize: 11, fontWeight: 600 }}>
                        {sm.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: 'var(--ink-4)' }}>{fmtDate(e.due_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddFinancialModal projectId={projectId} members={members} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function AddFinancialModal({ projectId, members, onClose, onAdded }) {
  const [form, setForm] = useState({ entry_type: 'invoice', description: '', amount: '', status: 'pending', due_date: '', member_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fs = { width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, background: 'var(--card)', fontSize: 13 };

  async function submit(e) {
    e.preventDefault();
    if (!form.amount) { setError('Amount required'); return; }
    setSaving(true);
    setError('');
    try {
      await projectAPI.createFinancial(projectId, {
        entry_type: form.entry_type,
        description: form.description,
        amount: parseFloat(form.amount),
        status: form.status,
        due_date: form.due_date || undefined,
        member_id: form.member_id || undefined,
        notes: form.notes || undefined,
      });
      onAdded();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3 className="serif" style={{ fontSize: 18, color: 'var(--forest)', fontWeight: 600, margin: 0 }}>Add financial entry</h3>
          <button className="btn ghost sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
            {error && <div style={{ color: 'var(--crimson)', fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label>Type</label>
                <select style={fs} value={form.entry_type} onChange={set('entry_type')}>
                  {['budget', 'quote', 'invoice', 'payment', 'expense'].map((t) => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Status</label>
                <select style={fs} value={form.status} onChange={set('status')}>
                  {Object.entries(FINANCIAL_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Description *</label><input style={fs} value={form.description} onChange={set('description')} required /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field"><label>Amount ($) *</label><input style={fs} type="number" value={form.amount} onChange={set('amount')} placeholder="0.00" required /></div>
              <div className="field"><label>Due date</label><input style={fs} type="date" value={form.due_date} onChange={set('due_date')} /></div>
            </div>
            <div className="field">
              <label>Assign to</label>
              <select style={fs} value={form.member_id} onChange={set('member_id')}>
                <option value="">— none —</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : 'Add entry'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Project Modal ────────────────────────────────────────────────────────

const PROJECT_TYPES = [
  '', 'Residential', 'Commercial', 'Mixed Use', 'Subdivision',
  'Renovation', 'Townhouse', 'Apartment', 'Industrial', 'Retail',
];

const PROJECT_STATUSES = [
  { value: 'active',    label: 'Active' },
  { value: 'on_hold',   label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived',  label: 'Archived' },
];

function EditProjectModal({ projectId, board, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:               board.name               || '',
    project_type:       board.project_type       || '',
    status:             board.status             || 'active',
    address_line1:      board.address_line1      || '',
    suburb:             board.suburb             || '',
    state:              board.state              || '',
    postcode:           board.postcode           || '',
    country:            board.country            || 'AU',
    budget:             board.budget             != null ? String(board.budget) : '',
    start_date:         board.start_date         ? board.start_date.slice(0, 10)         : '',
    target_completion:  board.target_completion  ? board.target_completion.slice(0, 10)  : '',
    description:        board.description        || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fs = {
    width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px',
    border: '1px solid var(--hairline)', borderRadius: 4,
    background: 'var(--card)', fontSize: 13,
  };
  const lbl = (text) => (
    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>
      {text}
    </div>
  );

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Project name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await projectAPI.update(projectId, {
        name:              form.name.trim(),
        project_type:      form.project_type      || undefined,
        status:            form.status,
        address_line1:     form.address_line1     || undefined,
        suburb:            form.suburb            || undefined,
        state:             form.state             || undefined,
        postcode:          form.postcode          || undefined,
        country:           form.country           || undefined,
        budget:            form.budget            ? parseFloat(form.budget) : undefined,
        start_date:        form.start_date        || undefined,
        target_completion: form.target_completion || undefined,
        description:       form.description       || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>Edit project</div>
            <h2 className="serif" style={{ fontSize: 20, margin: '2px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
              {board.name}
            </h2>
          </div>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
            {error && (
              <div style={{ padding: '8px 12px', background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13 }}>
                {error}
              </div>
            )}

            <div className="field">
              {lbl('Project name *')}
              <input style={fs} value={form.name} onChange={set('name')} required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="field">
                {lbl('Project type')}
                <select style={fs} value={form.project_type} onChange={set('project_type')}>
                  {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t || '— select —'}</option>)}
                </select>
              </div>
              <div className="field">
                {lbl('Status')}
                <select style={fs} value={form.status} onChange={set('status')}>
                  {PROJECT_STATUSES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <div className="field">
                {lbl('Budget ($)')}
                <input style={fs} type="number" value={form.budget} onChange={set('budget')} placeholder="e.g. 850000" />
              </div>
            </div>

            <div className="field">
              {lbl('Address')}
              <input style={fs} value={form.address_line1} onChange={set('address_line1')} placeholder="Street address" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
              <div className="field">
                {lbl('Suburb')}
                <input style={fs} value={form.suburb} onChange={set('suburb')} placeholder="Suburb" />
              </div>
              <div className="field">
                {lbl('State')}
                <input style={fs} value={form.state} onChange={set('state')} placeholder="NSW" />
              </div>
              <div className="field">
                {lbl('Postcode')}
                <input style={fs} value={form.postcode} onChange={set('postcode')} placeholder="2000" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                {lbl('Start date')}
                <input style={fs} type="date" value={form.start_date} onChange={set('start_date')} />
              </div>
              <div className="field">
                {lbl('Target completion')}
                <input style={fs} type="date" value={form.target_completion} onChange={set('target_completion')} />
              </div>
            </div>

            <div className="field">
              {lbl('Description')}
              <textarea
                value={form.description}
                onChange={set('description')}
                placeholder="Brief overview of the project…"
                style={{ ...fs, height: 72, padding: '8px 10px', resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Activity Tab ──────────────────────────────────────────────────────────────

function ActivityTab({ projectId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectAPI.listActivity(projectId)
      .then(({ data }) => setActivities(data))
      .finally(() => setLoading(false));
  }, [projectId]);

  const ACTION_ICONS = {
    'project.created': '🏗',
    'project.updated': '✏',
    'stage.created':   '📌',
    'task.created':    '➕',
    'task.moved':      '→',
    'task.updated':    '✏',
    'task.commented':  '💬',
    'member.added':    '👤',
    'financial.created': '💳',
  };

  function describeAction(a) {
    const d = a.detail || {};
    switch (a.action) {
      case 'project.created':   return `Created project "${d.name}"`;
      case 'project.updated':   return 'Updated project details';
      case 'stage.created':     return `Added stage "${d.name}"`;
      case 'task.created':      return `Created task "${d.title}" in ${d.stage}`;
      case 'task.moved':        return `Moved "${d.title}" from ${d.from} → ${d.to}`;
      case 'task.updated':      return `Updated task "${d.title}"`;
      case 'task.commented':    return `Commented on "${d.title}"`;
      case 'member.added':      return `Added ${d.name}${d.role ? ` (${d.role})` : ''} to team`;
      case 'financial.created': return `Added ${d.type} of ${fmtAud(d.amount)} — ${d.description}`;
      default: return a.action;
    }
  }

  return (
    <div style={{ padding: '24px 28px', overflowY: 'auto', height: '100%' }}>
      <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: 'var(--forest)', marginBottom: 20 }}>Activity log</div>

      {loading ? (
        <div style={{ color: 'var(--ink-4)', fontSize: 13 }}>Loading…</div>
      ) : activities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-4)', fontSize: 14 }}>No activity yet.</div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 18, top: 0, bottom: 0, width: 1, background: 'var(--hairline)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {activities.map((a) => (
              <div key={a.id} style={{ display: 'flex', gap: 16, paddingBottom: 18, position: 'relative' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'var(--card)', border: '2px solid var(--hairline)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0, position: 'relative', zIndex: 1,
                }}>
                  {ACTION_ICONS[a.action] || '•'}
                </div>
                <div style={{ paddingTop: 6, flex: 1 }}>
                  <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.4 }}>
                    {describeAction(a)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 3 }}>
                    {a.actor_name} · {fmtDate(a.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
